'use strict';
import isUndefined from 'lodash/isUndefined';
import noop from 'lodash/noop';
import {Observable} from 'rxjs/Observable';
import 'rxjs/add/observable/combineLatest';
import 'rxjs/add/observable/empty';
import 'rxjs/add/observable/merge';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/distinctUntilChanged';
import 'rxjs/add/operator/filter';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/switchMap';
import {getCollection, getId} from '../../core-utilities/domain/ReduxUtils';
import {eventToObservable, observeNewOnScope} from '../../core-utilities/rx-utils';
import {SCOPE_DESTROY} from '../../config/constants/view-events';
import {getUser} from '../../app/model/reducers/state';
import {CommentIndicatorComponent} from '../components/CommentIndicator/CommentIndicatorComponent';
import {reactReduxModal as chatChannelModal} from '../redux/chatChannelModal';


module.exports.directive = [
  CommentIndicatorDirective];

module.exports.controller = [
  '$scope',
  '$element',
  '$attrs',
  '$exceptionHandler',
  '$window',
  '$ngRedux',
  '$timeout',
  '$persistentCacheFactory',
  'ConversationDomain',
  CommentIndicatorController];

const CACHE_ID_COMMENTS = "CommentIndicatorDirective-comments-metrics";
const CACHE_ID_USER_COMMENTS = "CommentIndicatorDirective-user-comments-metrics";

function CommentIndicatorDirective() {
  return {
    restrict: 'E',
    require: ['commentIndicator', '?^^commentIndicatorContainer'],
    scope: {
      commentable: '=',
      hasUnreadComments: '=?',
      allowNav: '@'
    },
    controllerAs: 'commentIndicator',
    bindToController: true,
    controller: 'CommentIndicatorController',
    link: function (scope, element, attr, ctrls) {
      var commentCtrl = ctrls[0];
      var containerCtrl = ctrls[1];
      if (containerCtrl) containerCtrl.register(commentCtrl);

      element.on('click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        function callback() {
          commentCtrl.showComments();
        }

        scope.$apply(callback);
      });
    }
  }
}

function CommentIndicatorController(
  $scope,
  $element,
  $attrs,
  $exceptionHandler,
  $window,
  $ngRedux,
  $timeout,
  $persistentCacheFactory,
  ConversationDomain,
) {
  const vm = this;
  let triedToLink = false;
  let hasComments = false;

  this.showComments = showComments;

  this.hasUnreadComments = false;
  const cacheOptions = {
    capacity: 10000
  };
  const commentsMetricsCache = $persistentCacheFactory.get(CACHE_ID_COMMENTS) || $persistentCacheFactory(CACHE_ID_COMMENTS, cacheOptions);
  const userCommentsMetricsCache = $persistentCacheFactory.get(CACHE_ID_USER_COMMENTS) || $persistentCacheFactory(CACHE_ID_USER_COMMENTS, cacheOptions);

  $timeout(initModel, 0, false);

  function initModel() {
    observeNewOnScope($scope, () => getId(vm.commentable))
      .filter(id => !!id)
      .distinctUntilChanged()
      .switchMap(initCommentsObservers)
      .subscribe(() => null);
  }


  function showComments() {
    if (vm.commentable) {
      $ngRedux.dispatch(chatChannelModal.actions.showModal({
        target: {id: getId(vm.commentable), collection: getCollection(vm.commentable)},
        allowNav: !isUndefined(vm.allowNav) && (vm.allowNav == 'true' || vm.allowNav == true),
      }));
    }
  }

  function initCommentsObservers(commentableId) {
    ConversationDomain.checkName(commentableId);
    // const timeStart = window.performance.now();
    const cachedCommentCount = commentsMetricsCache.get(commentableId);
    const cachedReadCount = userCommentsMetricsCache.get(commentableId);
    // console.log(`METRICS: CommentIndicatorDirective.init(${commentableId}).readCache: ${window.performance.now() - timeStart}ms`);

    const rootState = $ngRedux.getState();
    const userId = getId(getUser(rootState));
    const data$ = Observable
      .combineLatest(
        CommentIndicatorComponent._buildCommentCount$(commentableId).startWith(cachedCommentCount || 0),
        CommentIndicatorComponent._buildReadCount$(userId, commentableId).startWith(cachedReadCount || 0),
        (commentCount, readCount) => {
          commentsMetricsCache.put(commentableId, commentCount);
          userCommentsMetricsCache.put(commentableId, readCount);

          displayHasComments(commentCount > 0);

          updateComments(commentCount - readCount);
          // console.log(`METRICS: CommentIndicatorDirective.init(${commentableId}).update   : ${window.performance.now() - timeStart}ms`);
        }
      )
      .takeUntil(eventToObservable($scope, SCOPE_DESTROY))
      .catch((error, source$) => {
        // Try to link the conversation target once then give up
        if (!triedToLink && vm.commentable && error && error.statusCode === 403) {
          triedToLink = true;
          return Observable.fromPromise(ConversationDomain.getLinked(commentableId, getCollection(vm.commentable)))
            .mergeMap(() => source$)
        } else {
          removeUserComments(commentableId);
          return Observable.empty();
        }
      });

    // console.log(`METRICS: CommentIndicatorDirective.init(${commentableId}).init     : ${window.performance.now() - timeStart}ms`);
    return data$;
  }

  function removeUserComments(commentableId) {
    const rootState = $ngRedux.getState();
    const user = getUser(rootState);
    // cleanup user metrics
    user.removeCommentsRef(commentableId).then(noop, function onCleanupError(e) {
      $exceptionHandler(e, 'CommentIndicatorDirective.commentsObservers error occurred in comment count observer');
    });
  }

  function updateComments(newDelta) {
    let hasChange = false;
    if (newDelta > 0) {
      animateNewComment();

      if (!vm.hasUnreadComments && $attrs.hasUnreadClass) {
        vm.hasUnreadComments = true;
        hasChange = true;
        //console.log('CommentIndicatorDirective.updateComments: removing class');
        $element.addClass($attrs.hasUnreadClass);
      }
    } else {
      if (vm.hasUnreadComments && $attrs.hasUnreadClass) {
        vm.hasUnreadComments = false;
        hasChange = true;
        //console.log('CommentIndicatorDirective.updateComments: adding class', $attrs.hasUnreadClass);
        $element.removeClass($attrs.hasUnreadClass);
      }
    }
    if (hasChange) {
      $scope.$evalAsync();
    }
  }

  function animateNewComment() {
    if ($attrs.onNewUnreadClass) {
      $element.addClass($attrs.onNewUnreadClass);
      $timeout(() => $element.removeClass($attrs.onNewUnreadClass), 1000);
    }
  }

  function displayHasComments(update) {
    if (hasComments === update) return;
    hasComments = update;
    $window.requestAnimationFrame(function () {
      if (update) {
        $element.removeClass('ion-ios-chatboxes-outline');
        $element.addClass('ion-ios-chatboxes');
      } else {
        $element.addClass('ion-ios-chatboxes-outline');
        $element.removeClass('ion-ios-chatboxes');
      }
    });
  }
}



// WEBPACK FOOTER //
// ./src/js/comments/directives/CommentIndicatorDirective.js